import type { HTMLAttributes, MutableRefObject, ReactNode, RefObject } from 'react'
import React, {
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import type { ComboboxItemProps, ComboboxPopoverOptions } from '@ariakit/react'
import { Combobox, ComboboxItem, ComboboxPopover, Portal, useComboboxContext } from '@ariakit/react'
import { filterWords } from '@udecode/plate-combobox'
import {
    useComboboxInput,
    UseComboboxInputResult,
    useHTMLInputCursorState,
} from '@udecode/plate-combobox/react'
import type { TElement } from '@udecode/plate-common'
import {
    createPointRef,
    getPointBefore,
    insertText,
    isHotkey,
    moveSelection,
} from '@udecode/plate-common'
import { findNodePath, useComposedRef, useEditorRef } from '@udecode/plate-common/react'
import { Box } from '@ui'
import { clsx } from 'clsx'
import { useDevice } from 'hooks/useDevice'

import { mentionInputInner, mentionInputInvisibleSpan } from '../../../RichTextEditor.css'
import { dispatchMockEnterEvent } from '../../../utils/helpers'

import { typeaheadMenuWrapper, typeaheadMenuWrapperFullWidth } from './Typeahead/Typeahead.css'
import { onMentionSelectTriggerMap } from './helpers'
import { TypeaheadMenuItem, TypeaheadMenuItemProps } from './Typeahead'
import { FilterFn, TComboboxItemWithData } from './types'

interface InlineComboboxContextValue {
    filter: FilterFn | false
    inputProps: UseComboboxInputResult['props']
    inputRef: RefObject<HTMLInputElement>
    cancelInput: UseComboboxInputResult['cancelInput']
    removeInput: UseComboboxInputResult['removeInput']
    showTrigger: boolean
    trigger: string
    inputRemovedRef: MutableRefObject<boolean>
}

const InlineComboboxContext = createContext<InlineComboboxContextValue>(null as never)

export const defaultFilter: FilterFn = ({ keywords = [], value }, search) =>
    [value, ...keywords].some((keyword) => filterWords(keyword, search))

interface InlineComboboxProps {
    children: ReactNode
    element: TElement
    trigger: string
    filter?: FilterFn | false
    hideWhenNoValue?: boolean
    setValue?: (value: string) => void
    showTrigger?: boolean
    value?: string
}

const InlineCombobox = ({
    children,
    element,
    filter = defaultFilter,
    showTrigger = true,
    trigger,
    value,
}: InlineComboboxProps) => {
    const editor = useEditorRef()
    const inputRef = React.useRef<HTMLInputElement>(null)
    const cursorState = useHTMLInputCursorState(inputRef as React.RefObject<HTMLInputElement>)

    // Track if input has been removed to prevent race conditions in setTimeout callbacks
    const inputRemovedRef = useRef(false)

    /**
     * Track the point just before the input element so we know where to
     * insertText if the combobox closes due to a selection change.
     */
    const insertPoint = useMemo(() => {
        const path = findNodePath(editor, element)

        if (!path) {
            return
        }

        const point = getPointBefore(editor, path)

        if (!point) {
            return
        }

        return createPointRef(editor, point)
    }, [editor, element])

    useEffect(() => {
        return () => {
            if (insertPoint) {
                insertPoint.unref()
            }
        }
    }, [insertPoint])

    const {
        props: inputProps,
        cancelInput,
        removeInput: baseRemoveInput,
    } = useComboboxInput({
        cancelInputOnBlur: true,
        cancelInputOnDeselect: true,
        cancelInputOnEscape: true,
        cursorState,
        onCancelInput: (cause) => {
            if (cause !== 'backspace') {
                insertText(editor, trigger + value, {
                    at: insertPoint?.current ?? undefined,
                })
            }
            if (cause === 'arrowLeft' || cause === 'arrowRight') {
                moveSelection(editor, {
                    distance: 1,
                    reverse: cause === 'arrowLeft',
                })
            }
        },
        ref: inputRef as RefObject<HTMLInputElement>,
    })

    // Wrapper that tracks when input is removed to prevent race conditions
    const removeInput = useCallback(
        (focusEditor?: boolean) => {
            inputRemovedRef.current = true
            baseRemoveInput(focusEditor)
        },
        [baseRemoveInput],
    )

    const contextValue: InlineComboboxContextValue = useMemo(
        () => ({
            filter,
            inputProps,
            inputRef: inputRef as RefObject<HTMLInputElement>,
            removeInput,
            cancelInput,
            showTrigger,
            trigger,
            inputRemovedRef,
        }),
        [trigger, showTrigger, filter, inputRef, inputProps, cancelInput, removeInput],
    )

    return (
        <span contentEditable={false}>
            <InlineComboboxContext.Provider value={contextValue}>
                {children}
            </InlineComboboxContext.Provider>
        </span>
    )
}

const InlineComboboxInput = forwardRef<
    HTMLInputElement,
    HTMLAttributes<HTMLInputElement> & { resultsLength: number }
>(({ className, resultsLength, ...props }, propRef) => {
    const {
        inputProps,
        inputRef: contextRef,
        showTrigger,
        trigger,
        cancelInput,
        removeInput,
        inputRemovedRef,
    } = useContext(InlineComboboxContext)
    const { onKeyDown } = inputProps
    const editor = useEditorRef()
    const { isTouch } = useDevice()
    const store = useComboboxContext()!
    const value = store.useState('value')

    const ref = useComposedRef(propRef, contextRef)

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            /*
             * On touch devices, especially Safari iOS, the input is not removed when user presses Backspace on an
             * empty input. This is a workaround to remove the input in that case.
             *
             * Do NOT use isHotkey here because it doesn't work on Safari. The KeyUp event is fired too quickly
             * for the plugin to detect the Backspace key.
             */
            if (isTouch && event.key === 'Backspace' && contextRef.current?.value === '') {
                event.preventDefault()
                event.stopPropagation()
                removeInput(true)
                return
            }

            /**
             * If there is only one item suggestion when user presses space, and the typed value is the same
             * as the suggestion, select it
             */
            if (isHotkey('Space', event) && resultsLength === 1) {
                const firstResult = store.getState().items?.[0] as unknown as TComboboxItemWithData
                if (
                    firstResult &&
                    store.getState().value.toLowerCase() === firstResult.text.toLowerCase()
                ) {
                    removeInput(true)
                    onMentionSelectTriggerMap(trigger)?.(editor, firstResult, firstResult.text)
                    return
                }
            }

            /** Select the currently active item value using Tab key */
            if (isHotkey('Tab', event) && store.getState().activeId) {
                const activeItem = store.item(
                    store.getState().activeId,
                ) as unknown as TComboboxItemWithData
                if (!activeItem) {
                    return
                }
                event.preventDefault()
                event.stopPropagation()
                removeInput(true)
                onMentionSelectTriggerMap(trigger)?.(editor, activeItem, activeItem.text)
                return
            }

            /**
             * If there are no item suggestion when user presses enter or space, remove the input
             * and combobox, append the existing value to the editor as text node (handled in cancelInput)
             *
             * In the end we dispatch that keyboard event to the editor because combobox events are not
             * propagated to the editor by default. SetTimeout is used to ensure that editor migrations
             * are done before the event is dispatched.
             *
             * This needs to be wrapped in a setTimeout because the store is updated asynchronously after the
             * keydown event fires. This is a workaround to ensure that the store updates before we check
             * the updated results length.
             */
            setTimeout(() => {
                // Guard against race condition: if input was already removed (e.g., by item selection),
                // don't try to cancel it again as the node no longer exists
                if (inputRemovedRef.current) {
                    return
                }
                const updatedResultsLength = store.getState().items.length
                if (
                    (isHotkey('Enter', event) &&
                        (!store.getState().activeValue || updatedResultsLength === 0)) ||
                    (isHotkey('Space', event) && updatedResultsLength === 0)
                ) {
                    event.preventDefault()
                    event.stopPropagation()
                    cancelInput('manual', true)
                    if (isHotkey('Enter', event)) {
                        setTimeout(dispatchMockEnterEvent, 10)
                    } else {
                        editor.insertText(' ')
                    }
                    return
                }
            }, 100)

            onKeyDown?.(event)
        },
        [
            isTouch,
            contextRef,
            resultsLength,
            onKeyDown,
            removeInput,
            inputRemovedRef,
            store,
            trigger,
            editor,
            cancelInput,
        ],
    )

    /**
     * To create an auto-resizing input, we render a visually hidden span
     * containing the input value and position the input element on top of it.
     * This works well for all cases except when input exceeds the width of the
     * container.
     */

    return (
        <>
            {showTrigger && (
                <Box as="span" display="inline-block" className={className}>
                    {trigger}
                </Box>
            )}
            <Box as="span" position="relative" display="inline-block">
                <Box
                    as="span"
                    aria-hidden="true"
                    className={clsx([className, mentionInputInvisibleSpan])}
                >
                    {value || '\u200B'}
                </Box>

                <Combobox
                    autoSelect
                    {...inputProps}
                    {...props}
                    ref={ref}
                    value={value}
                    data-testid="editor-autosuggest-input"
                    className={clsx([mentionInputInner, className])}
                    onKeyDown={handleKeyDown}
                    // Stop composition events from propagating to Slate editor
                    // to prevent duplicate text input on mobile devices.
                    // This mirrors the fix in slate-react 0.118.2 which ignores
                    // composition events from nested input/textarea elements.
                    onCompositionStart={(e) => e.stopPropagation()}
                    onCompositionUpdate={(e) => e.stopPropagation()}
                    onCompositionEnd={(e) => e.stopPropagation()}
                />
            </Box>
        </>
    )
})

InlineComboboxInput.displayName = 'InlineComboboxInput'

const InlineComboboxContent = ({
    className,
    lockPosition,
    fullWidth,
    ...props
}: ComboboxPopoverOptions & {
    children: ReactNode
    lockPosition: boolean
    className: string
    fullWidth?: boolean
}) => {
    const anchorRef = React.useRef<DOMRect | null>(null)
    const [containerWidth, setContainerWidth] = React.useState<number | null>(null)
    const getAnchorRect = React.useCallback(
        (anchor: HTMLElement | null) => {
            if (!anchor) {
                return null
            }
            // For full-width mode, use the editor container as anchor
            if (fullWidth) {
                const editorContainer = anchor.closest('[data-testid="editor-container"]')
                if (editorContainer) {
                    const rect = editorContainer.getBoundingClientRect()
                    setContainerWidth(rect.width)
                    return rect
                }
            }

            if (lockPosition) {
                if (!anchorRef.current) {
                    anchorRef.current = anchor.getBoundingClientRect()
                }
                return anchorRef.current
            }
            return null
        },
        [lockPosition, fullWidth],
    )

    // Portal prevents CSS from leaking into popover
    return (
        <Portal>
            <ComboboxPopover
                {...props}
                data-testid="editor-autosuggest-popover"
                className={clsx([
                    typeaheadMenuWrapper,
                    fullWidth && typeaheadMenuWrapperFullWidth,
                    className,
                ])}
                style={fullWidth && containerWidth ? { width: containerWidth } : undefined}
                getAnchorRect={lockPosition || fullWidth ? getAnchorRect : undefined}
            />
        </Portal>
    )
}

export type InlineComboboxItemProps = {
    keywords?: string[]
} & ComboboxItemProps &
    Required<Pick<ComboboxItemProps, 'value'>> &
    TypeaheadMenuItemProps

const InlineComboboxItem = ({
    className,
    keywords,
    onClick,
    labelIcon,
    ...props
}: InlineComboboxItemProps) => {
    const { removeInput } = useContext(InlineComboboxContext)
    const { isTouch } = useDevice()

    const onSelectOption: React.MouseEventHandler<HTMLDivElement> = useCallback(
        (event) => {
            removeInput(true)
            onClick?.(event)
        },
        [onClick, removeInput],
    )

    return (
        <ComboboxItem
            {...props}
            render={<TypeaheadMenuItem labelIcon={labelIcon} />}
            onClick={isTouch ? undefined : onSelectOption}
            onMouseDown={isTouch ? onSelectOption : undefined}
        />
    )
}

export { InlineCombobox, InlineComboboxContent, InlineComboboxInput, InlineComboboxItem }
